import { useEffect, useMemo, useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import { computeArchetypeProfile } from "@/lib/karma/archetypes";
import { getDigitShortLabel, getProfileInsights } from "@/lib/karma/insights";
import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import type { KarmaBirthDate } from "@/lib/karma/types";
import { getMe, updateUser } from "@/lib/karma/storage";
import { fetchProgressSummary, fetchProgressTimeline } from "@/lib/progress";
import {
  getSupabaseClient,
  getSupabaseConfig,
  getSupabaseStorageKeyFromDirectUrl,
} from "@/lib/supabase/client";
import { listBlockedUsersInDb, unblockUserInDb } from "@/lib/supabase/social";
import { cn } from "@/lib/utils";
import { calculateYearForesight, getElement } from "@shared/numerology";
import { Camera, Check, ChevronDown, ChevronRight, Loader2, Trash2, User as UserIcon, X } from "lucide-react";

type KarmaProfileRow = {
  id: string;
  handle: string;
  display_name: string;
  birth_date: string | null;
  bio: string | null;
  photo_url: string | null;
  match_enabled: boolean;
  gender?: "kadin" | "erkek" | "non_binary" | "belirtmek_istemiyorum" | null;
  preferred_gender?: "kadin" | "erkek" | "non_binary" | "herkes" | null;
  pref_min_age?: number | null;
  pref_max_age?: number | null;
  pref_only_match_enabled?: boolean | null;
  pref_destiny_numbers?: number[] | null;
  pref_heart_desire_numbers?: number[] | null;
  pref_expression_numbers?: number[] | null;
  pref_karmic_debt_numbers?: number[] | null;
  // Extras (optional until DB schema is updated)
  birth_time?: string | null;
  birth_place?: string | null;
  interests?: string[] | null;
  location_matching?: boolean | null;
  saved_addresses?: SavedAddress[] | null;
  selected_address_id?: string | null;
  created_at?: string;
};

type SavedAddressLabel = "ev" | "is" | "diger";

type SavedAddress = {
  id: string;
  label: SavedAddressLabel;
  title: string;
  identity_number: string;
  phone: string;
  address_line1: string;
  city: string;
  district: string;
  postcode: string;
};

type ProfileExtras = {
  birth_time: string;
  birth_place: string;
  interests: string[];
  location_matching: boolean;
  saved_addresses: SavedAddress[];
  selected_address_id: string;
};

type ProfileMissingField =
  | "photo"
  | "display_name"
  | "birth_date"
  | "bio"
  | "birth_time"
  | "birth_place"
  | "interests";

type SettingsTab = "personal" | "addresses" | "numerology" | "matching" | "interests" | "security";

const EXTRAS_STORAGE_KEY = "panel_profile_extras_v1";
const LOCAL_PROFILE_STORAGE_KEY = "panel_profile_local_v1";

type MatchingPreferencesDraft = {
  prefMinAge: string;
  prefMaxAge: string;
  prefOnlyMatchEnabled: boolean;
  prefOnlySimilarStatus: boolean;
  prefPartnerGender: "kadin" | "erkek" | "non_binary" | "herkes";
  prefDestinyNumbers: number[];
  prefHeartDesireNumbers: number[];
  prefExpressionNumbers: number[];
  prefKarmicDebtNumbers: number[];
};

function normalizePreferenceDigits(input: number[]) {
  const unique = Array.from(new Set(input.map((n) => Math.trunc(Number(n))).filter((n) => Number.isFinite(n) && n >= 1 && n <= 9)));
  return unique.length ? unique : undefined;
}

function normalizeGender(value: unknown): "kadin" | "erkek" | "non_binary" | "belirtmek_istemiyorum" | undefined {
  if (value === "kadin" || value === "erkek" || value === "non_binary" || value === "belirtmek_istemiyorum") return value;
  return undefined;
}

function normalizePreferredGender(value: unknown): "kadin" | "erkek" | "non_binary" | "herkes" {
  if (value === "kadin" || value === "erkek" || value === "non_binary" || value === "herkes") return value;
  return "herkes";
}

function buildMatchingPreferencesDraft(me: ReturnType<typeof getMe>): MatchingPreferencesDraft {
  const minAge = Math.max(18, Math.min(99, Number(me?.prefMinAge ?? 18) || 18));
  const maxAge = Math.max(minAge, Math.min(99, Number(me?.prefMaxAge ?? 99) || 99));

  return {
    prefMinAge: String(minAge),
    prefMaxAge: String(maxAge),
    prefOnlyMatchEnabled: Boolean(me?.prefOnlyMatchEnabled),
    prefOnlySimilarStatus: Boolean(me?.prefOnlySimilarStatus),
    prefPartnerGender: normalizePreferredGender((me as any)?.prefPartnerGender),
    prefDestinyNumbers: Array.isArray(me?.prefDestinyNumbers) ? me.prefDestinyNumbers : [],
    prefHeartDesireNumbers: Array.isArray(me?.prefHeartDesireNumbers) ? me.prefHeartDesireNumbers : [],
    prefExpressionNumbers: Array.isArray(me?.prefExpressionNumbers) ? me.prefExpressionNumbers : [],
    prefKarmicDebtNumbers: Array.isArray(me?.prefKarmicDebtNumbers) ? me.prefKarmicDebtNumbers : [],
  };
}

function withTimeout<T>(promise: PromiseLike<T>, ms: number, message: string) {
  let timeoutId: number | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(message)), ms);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) window.clearTimeout(timeoutId);
  }) as Promise<T>;
}

function normalizeHandle(value: string) {
  return value
    .toLocaleLowerCase("tr-TR")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z_]+/g, "")
    .slice(0, 24);
}

function formatDisplayNameFromHandle(value: string) {
  return String(value ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (ch) => ch.toLocaleUpperCase("tr-TR"));
}

function isGenericDisplayName(value: string) {
  const normalized = String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return true;
  if (["kullanıcı", "kullanici", "user", "biri", "üye", "uye"].includes(normalized)) return true;
  return normalized === "bir fincan sevgi" || normalized.startsWith("bir fincan sevgi ");
}

function isHandleLikeDisplayName(displayName: string, handle: string) {
  const rawDisplay = String(displayName ?? "").trim();
  if (!rawDisplay) return false;

  // If user-entered display name contains spaces, treat it as a real full name.
  if (/\s/.test(rawDisplay)) return false;

  const normalizedDisplay = normalizeHandle(rawDisplay);
  const normalizedHandle = normalizeHandle(handle);
  if (!normalizedDisplay || !normalizedHandle) return false;
  return normalizedDisplay === normalizedHandle;
}

function pickDisplayNameFromMetadata(metadata: Record<string, unknown>) {
  const first = typeof metadata.first_name === "string" ? metadata.first_name.trim() : "";
  const last = typeof metadata.last_name === "string" ? metadata.last_name.trim() : "";
  const full = `${first} ${last}`.trim();
  if (full) return full;

  const fullName = typeof metadata.full_name === "string" ? metadata.full_name.trim() : "";
  if (fullName) return fullName;

  const name = typeof metadata.name === "string" ? metadata.name.trim() : "";
  if (name) return name;

  const displayName = typeof metadata.display_name === "string" ? metadata.display_name.trim() : "";
  if (displayName) return displayName;

  return "";
}

function resolveProfileDisplayName(displayName: string, handle: string) {
  const cleanDisplayName = String(displayName ?? "").trim();
  if (!isGenericDisplayName(cleanDisplayName) && !isHandleLikeDisplayName(cleanDisplayName, handle)) return cleanDisplayName;
  return formatDisplayNameFromHandle(handle) || cleanDisplayName || "Kullanıcı";
}

function parseBirthDateFromRow(value: string | null): KarmaBirthDate | null {
  if (!value) return null;
  const [y, m, d] = value.split("-").map((v) => Number(v));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return { year: y, month: m, day: d };
}

function toIsoBirthDate(bd: KarmaBirthDate | null) {
  if (!bd) return null;
  const parsed = parseBirthDate(bd);
  const yyyy = String(parsed.year).padStart(4, "0");
  const mm = String(parsed.month).padStart(2, "0");
  const dd = String(parsed.day).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function toIsoBirthDateFromMetadata(metadata: Record<string, unknown>) {
  const dayRaw = Number(metadata.birth_day ?? metadata.birthDay ?? 0);
  const monthRaw = Number(metadata.birth_month ?? metadata.birthMonth ?? 0);
  const yearRaw = Number(metadata.birth_year ?? metadata.birthYear ?? 0);

  if (Number.isFinite(dayRaw) && Number.isFinite(monthRaw) && Number.isFinite(yearRaw)) {
    const parsed = parseBirthDate({ day: dayRaw, month: monthRaw, year: yearRaw });
    return toIsoBirthDate(parsed);
  }

  const raw = metadata.birth_date ?? metadata.birthDate ?? metadata.dob;
  const text = typeof raw === "string" ? raw.trim() : "";
  if (!text) return null;

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const dotMatch = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (dotMatch) {
    const day = Number(dotMatch[1]);
    const month = Number(dotMatch[2]);
    const year = Number(dotMatch[3]);
    const parsed = parseBirthDate({ day, month, year });
    return toIsoBirthDate(parsed);
  }

  return null;
}

function formatBirthText(bd: KarmaBirthDate | null) {
  if (!bd) return "";
  const parsed = parseBirthDate(bd);
  return `${parsed.day}.${parsed.month}.${parsed.year}`;
}

function parseBirthDateParts(value: string) {
  const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(String(value || ""));
  if (!match) return { year: "", month: "", day: "" };
  return { year: match[1], month: match[2], day: match[3] };
}

function getDaysInMonth(year: number, month: number) {
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return 31;
  return new Date(year, month, 0).getDate();
}

function buildIsoBirthDateFromParts(parts: { year: string; month: string; day: string }) {
  if (!parts.year || !parts.month || !parts.day) return "";
  const parsed = parseBirthDate({
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
  });
  return toIsoBirthDate(parsed) ?? "";
}

function normalizeSavedAddressLabel(input: unknown): SavedAddressLabel {
  return input === "ev" || input === "is" || input === "diger" ? input : "ev";
}

function createEmptySavedAddress(): SavedAddress {
  return {
    id: `addr_${Math.random().toString(36).slice(2, 10)}`,
    label: "ev",
    title: "",
    identity_number: "",
    phone: "",
    address_line1: "",
    city: "",
    district: "",
    postcode: "",
  };
}

function normalizeSavedAddresses(input: unknown): SavedAddress[] {
  if (!Array.isArray(input)) return [];
  return input
    .map((entry: any) => {
      const id = String(entry?.id ?? "").trim() || `addr_${Math.random().toString(36).slice(2, 10)}`;
      return {
        id,
        label: normalizeSavedAddressLabel(entry?.label),
        title: typeof entry?.title === "string" ? entry.title : "",
        identity_number: typeof entry?.identity_number === "string" ? entry.identity_number.replace(/[^0-9]/g, "").slice(0, 11) : "",
        phone: typeof entry?.phone === "string" ? entry.phone : "",
        address_line1: typeof entry?.address_line1 === "string" ? entry.address_line1 : "",
        city: typeof entry?.city === "string" ? entry.city : "",
        district: typeof entry?.district === "string" ? entry.district : "",
        postcode: typeof entry?.postcode === "string" ? entry.postcode : "",
      } satisfies SavedAddress;
    })
    .filter((entry: SavedAddress) =>
      Boolean(
        entry.title.trim() ||
          entry.identity_number.trim() ||
          entry.phone.trim() ||
          entry.address_line1.trim() ||
          entry.city.trim() ||
          entry.district.trim() ||
          entry.postcode.trim(),
      ),
    );
}

function loadExtras(userId: string): ProfileExtras {
  try {
    const raw = globalThis.localStorage?.getItem(EXTRAS_STORAGE_KEY);
    if (!raw) {
      return { birth_time: "", birth_place: "", interests: [], location_matching: false, saved_addresses: [], selected_address_id: "" };
    }

    const parsed = JSON.parse(raw) as any;
    const mine = parsed?.[userId];

    const savedAddresses = normalizeSavedAddresses(mine?.saved_addresses);

    const selectedAddressId = typeof mine?.selected_address_id === "string" ? mine.selected_address_id : "";

    return {
      birth_time: typeof mine?.birth_time === "string" ? mine.birth_time : "",
      birth_place: typeof mine?.birth_place === "string" ? mine.birth_place : "",
      interests: Array.isArray(mine?.interests) ? mine.interests.filter((v: any) => typeof v === "string") : [],
      location_matching: Boolean(mine?.location_matching),
      saved_addresses: savedAddresses,
      selected_address_id: savedAddresses.some((entry) => entry.id === selectedAddressId) ? selectedAddressId : savedAddresses[0]?.id ?? "",
    };
  } catch {
    return { birth_time: "", birth_place: "", interests: [], location_matching: false, saved_addresses: [], selected_address_id: "" };
  }
}

function saveExtras(userId: string, extras: ProfileExtras) {
  try {
    const raw = globalThis.localStorage?.getItem(EXTRAS_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as any) : {};
    parsed[userId] = extras;
    globalThis.localStorage?.setItem(EXTRAS_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // ignore
  }
}

function loadLocalProfile(userId: string): Partial<KarmaProfileRow> | null {
  try {
    const raw = globalThis.localStorage?.getItem(LOCAL_PROFILE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as any;
    const mine = parsed?.[userId];
    if (!mine || typeof mine !== "object") return null;

    const out: Partial<KarmaProfileRow> = {};
    if (typeof mine.bio === "string") out.bio = mine.bio;
    if (mine.bio === null) out.bio = null;
    if (typeof mine.photo_url === "string") out.photo_url = mine.photo_url;
    if (mine.photo_url === null) out.photo_url = null;
    if (typeof mine.match_enabled === "boolean") out.match_enabled = mine.match_enabled;
    if (mine.gender === null) out.gender = null;
    if (typeof mine.gender === "string") out.gender = normalizeGender(mine.gender) ?? null;
    if (mine.preferred_gender === null) out.preferred_gender = null;
    if (typeof mine.preferred_gender === "string") out.preferred_gender = normalizePreferredGender(mine.preferred_gender);

    return out;
  } catch {
    return null;
  }
}

function saveLocalProfile(userId: string, patch: Partial<KarmaProfileRow>) {
  try {
    const raw = globalThis.localStorage?.getItem(LOCAL_PROFILE_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as any) : {};
    const next = { ...(parsed?.[userId] ?? {}) } as Record<string, unknown>;

    if (typeof patch.bio === "string" || patch.bio === null) next.bio = patch.bio;
    if (typeof patch.photo_url === "string" || patch.photo_url === null) next.photo_url = patch.photo_url;
    if (typeof patch.match_enabled === "boolean") next.match_enabled = patch.match_enabled;
    if (typeof patch.gender === "string" || patch.gender === null) next.gender = patch.gender;
    if (typeof patch.preferred_gender === "string" || patch.preferred_gender === null) next.preferred_gender = patch.preferred_gender;

    delete next.display_name;
    delete next.birth_date;
    delete next.handle;

    parsed[userId] = next;
    globalThis.localStorage?.setItem(LOCAL_PROFILE_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // ignore
  }
}

function isAbortLikeError(error: unknown) {
  const anyErr = error as any;
  const name = String(anyErr?.name ?? "").toLowerCase();
  const message = String(anyErr?.message ?? "").toLowerCase();
  return name.includes("abort") || message.includes("abort") || message.includes("aborted") || message.includes("operation was aborted");
}

function buildFallbackProfile(currentUser: { id: string; email: string | null; metadata?: Record<string, unknown> }): KarmaProfileRow {
  const meta = (currentUser.metadata ?? {}) as any;
  const email = typeof currentUser.email === "string" ? currentUser.email : "";
  const displayNameRaw = pickDisplayNameFromMetadata(meta) || (email ? email.split("@")[0] : "Kullanıcı");

  const baseHandle = normalizeHandle(typeof meta.handle === "string" ? meta.handle : displayNameRaw || "user");
  const fallbackHandle = `user_${currentUser.id.slice(0, 8)}`;
  const displayName = resolveProfileDisplayName(displayNameRaw, baseHandle || fallbackHandle);

  const birthDay = typeof meta.birth_day === "number" ? meta.birth_day : Number(meta.birth_day);
  const birthMonth = typeof meta.birth_month === "number" ? meta.birth_month : Number(meta.birth_month);
  const birthYear = typeof meta.birth_year === "number" ? meta.birth_year : Number(meta.birth_year);

  let birthDate: string | null = null;
  if (Number.isFinite(birthDay) && Number.isFinite(birthMonth) && Number.isFinite(birthYear)) {
    const parsed = parseBirthDate({ day: birthDay, month: birthMonth, year: birthYear });
    birthDate = toIsoBirthDate(parsed);
  }

  const photoUrl = typeof meta.avatar_url === "string" ? meta.avatar_url : null;
  const gender = normalizeGender(meta.gender);
  const preferredGender = normalizePreferredGender(meta.preferred_gender);

  return {
    id: currentUser.id,
    handle: baseHandle || fallbackHandle,
    display_name: displayName,
    birth_date: birthDate,
    bio: null,
    photo_url: photoUrl,
    match_enabled: true,
    gender: gender ?? null,
    preferred_gender: preferredGender,
    created_at: undefined,
  };
}

async function ensureMyKarmaProfile(currentUser: { id: string; email: string | null; metadata?: Record<string, unknown> }) {
  const local = loadLocalProfile(currentUser.id);

  let supabase: ReturnType<typeof getSupabaseClient> | null = null;
  try {
    supabase = getSupabaseClient();
  } catch {
    return {
      ...buildFallbackProfile(currentUser),
      ...(local ?? {}),
    } satisfies KarmaProfileRow;
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const sessionUser = sessionData.session?.user;

  if (!sessionUser || sessionUser.id !== currentUser.id) {
    // Fallback mode (e.g. dev auth): use localStorage + auth metadata so UI doesn't get stuck.
    return {
      ...buildFallbackProfile(currentUser),
      ...(local ?? {}),
    } satisfies KarmaProfileRow;
  }

  const selectBase =
    "id, handle, display_name, birth_date, bio, photo_url, match_enabled, pref_min_age, pref_max_age, pref_only_match_enabled, pref_destiny_numbers, pref_heart_desire_numbers, pref_expression_numbers, pref_karmic_debt_numbers, created_at";
  const selectWithExtras = `${selectBase}, gender, preferred_gender, birth_time, birth_place, interests, location_matching, saved_addresses, selected_address_id`;

  let data: any = null;
  let error: any = null;

  {
    const res = await supabase.from("karma_profiles").select(selectWithExtras).eq("id", currentUser.id).maybeSingle();
    data = res.data;
    error = res.error;

    // If the DB doesn't have the extra columns yet, retry with the legacy select.
    const msg = String(error?.message ?? "");
    if (error && msg.includes("column") && (msg.includes("birth_time") || msg.includes("birth_place") || msg.includes("interests") || msg.includes("location_matching") || msg.includes("saved_addresses") || msg.includes("selected_address_id") || msg.includes("gender") || msg.includes("preferred_gender"))) {
      const legacy = await supabase.from("karma_profiles").select(selectBase).eq("id", currentUser.id).maybeSingle();
      data = legacy.data;
      error = legacy.error;
    }
  }

  if (!error && data) {
    const row = data as KarmaProfileRow;

    const metaFromSession = (sessionUser.user_metadata ?? {}) as Record<string, unknown>;
    const birthDateIsoFromMeta = toIsoBirthDateFromMetadata(metaFromSession);
    const displayNameFromMeta = pickDisplayNameFromMetadata(metaFromSession);
    const metadataGender = normalizeGender(metaFromSession.gender);
    const metadataPreferredGender = normalizePreferredGender(metaFromSession.preferred_gender);

    const patch: Record<string, unknown> = {};

    if (!row.birth_date && birthDateIsoFromMeta) {
      patch.birth_date = birthDateIsoFromMeta;
    }

    const rowDisplayName = String(row.display_name ?? "").trim();
    const rowHandle = String(row.handle ?? "").trim();
    const shouldRepairDisplayName =
      Boolean(displayNameFromMeta) && (isGenericDisplayName(rowDisplayName) || isHandleLikeDisplayName(rowDisplayName, rowHandle));

    if (shouldRepairDisplayName) {
      patch.display_name = displayNameFromMeta;
    }

    if (!row.gender && metadataGender) {
      patch.gender = metadataGender;
    }

    if (!row.preferred_gender && metadataPreferredGender) {
      patch.preferred_gender = metadataPreferredGender;
    }

    if (Object.keys(patch).length > 0) {
      const updateRes = await supabase.from("karma_profiles").update(patch).eq("id", currentUser.id);
      if (!updateRes.error) {
        const patched = { ...(row as any), ...(patch as any) } as KarmaProfileRow;
        saveLocalProfile(currentUser.id, patched as any);
        return patched;
      }
    }

    saveLocalProfile(currentUser.id, row as any);
    return row;
  }

  if (error) {
    return {
      ...buildFallbackProfile(currentUser),
      ...(local ?? {}),
    } satisfies KarmaProfileRow;
  }

  const meta = (sessionUser.user_metadata ?? {}) as any;

  const email = typeof sessionUser.email === "string" ? sessionUser.email : "";
  const displayNameRaw = pickDisplayNameFromMetadata(meta) || (email ? email.split("@")[0] : "Kullanıcı");

  const baseHandle = normalizeHandle(typeof meta.handle === "string" ? meta.handle : displayNameRaw || "user");
  const fallback = `user_${currentUser.id.slice(0, 8)}`;
  const base = baseHandle || fallback;
  const displayName = resolveProfileDisplayName(displayNameRaw, base);

  const handleCandidates = [base, `${base}_${currentUser.id.slice(0, 4)}`, `${base}_${currentUser.id.slice(0, 8)}`];

  const birthDay = typeof meta.birth_day === "number" ? meta.birth_day : Number(meta.birth_day);
  const birthMonth = typeof meta.birth_month === "number" ? meta.birth_month : Number(meta.birth_month);
  const birthYear = typeof meta.birth_year === "number" ? meta.birth_year : Number(meta.birth_year);

  let birthDate: string | null = null;
  if (Number.isFinite(birthDay) && Number.isFinite(birthMonth) && Number.isFinite(birthYear)) {
    const parsed = parseBirthDate({ day: birthDay, month: birthMonth, year: birthYear });
    birthDate = toIsoBirthDate(parsed);
  }

  const photoUrl = typeof meta.avatar_url === "string" ? meta.avatar_url.trim() : "";

  let upsertError: any = null;

  for (const handle of handleCandidates) {
    const payload: Record<string, unknown> = {
      id: currentUser.id,
      handle,
      display_name: displayName,
      birth_date: birthDate,
      bio: "",
      match_enabled: true,
      pref_min_age: 18,
      pref_max_age: 99,
      pref_only_match_enabled: false,
      pref_destiny_numbers: [],
      pref_heart_desire_numbers: [],
      pref_expression_numbers: [],
      pref_karmic_debt_numbers: [],
    };

    if (photoUrl) {
      payload.photo_url = photoUrl;
    }

    const res = await supabase.from("karma_profiles").upsert(payload, { onConflict: "id" });

    upsertError = res.error;
    if (!upsertError) break;
  }

  if (upsertError) {
    return {
      ...buildFallbackProfile(currentUser),
      ...(local ?? {}),
    } satisfies KarmaProfileRow;
  }

  const selectBaseCreated =
    "id, handle, display_name, birth_date, bio, photo_url, match_enabled, pref_min_age, pref_max_age, pref_only_match_enabled, pref_destiny_numbers, pref_heart_desire_numbers, pref_expression_numbers, pref_karmic_debt_numbers, created_at";
  const selectWithExtrasCreated = `${selectBaseCreated}, gender, preferred_gender, birth_time, birth_place, interests, location_matching, saved_addresses, selected_address_id`;

  let created: any = null;
  let createdError: any = null;

  {
    const res = await supabase.from("karma_profiles").select(selectWithExtrasCreated).eq("id", currentUser.id).single();
    created = res.data;
    createdError = res.error;

    const msg = String(createdError?.message ?? "");
    if (createdError && msg.includes("column") && (msg.includes("birth_time") || msg.includes("birth_place") || msg.includes("interests") || msg.includes("location_matching") || msg.includes("saved_addresses") || msg.includes("selected_address_id") || msg.includes("gender") || msg.includes("preferred_gender"))) {
      const legacy = await supabase.from("karma_profiles").select(selectBaseCreated).eq("id", currentUser.id).single();
      created = legacy.data;
      createdError = legacy.error;
    }
  }

  if (createdError) {
    return {
      ...buildFallbackProfile(currentUser),
      ...(local ?? {}),
    } satisfies KarmaProfileRow;
  }

  saveLocalProfile(currentUser.id, created as any);
  return created as KarmaProfileRow;
}

async function fetchPanelStats(currentUser: { id: string }) {
  let supabase: ReturnType<typeof getSupabaseClient> | null = null;
  try {
    supabase = getSupabaseClient();
  } catch {
    return { likesGiven: 0, likesReceived: 0, mutualMatches: 0 };
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const sessionUser = sessionData.session?.user;

  if (!sessionUser || sessionUser.id !== currentUser.id) {
    return { likesGiven: 0, likesReceived: 0, mutualMatches: 0 };
  }

  const [givenRes, receivedRes] = await Promise.all([
    supabase.from("karma_likes").select("to_user_id").eq("from_user_id", currentUser.id).limit(500),
    supabase.from("karma_likes").select("from_user_id").eq("to_user_id", currentUser.id).limit(500),
  ]);

  if (givenRes.error) throw givenRes.error;
  if (receivedRes.error) throw receivedRes.error;

  const given = new Set((givenRes.data ?? []).map((r: any) => String(r.to_user_id)));
  const received = new Set((receivedRes.data ?? []).map((r: any) => String(r.from_user_id)));

  let mutual = 0;
  for (const id of given) {
    if (received.has(id)) mutual += 1;
  }

  return {
    likesGiven: given.size,
    likesReceived: received.size,
    mutualMatches: mutual,
  };
}

function PortraitCard(props: { label: string; value: string; meta?: string; body: string }) {
  return (
    <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-base font-bold text-slate-900 dark:text-slate-100">{props.label}</div>
          {props.meta ? (
            <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{props.meta}</div>
          ) : null}
        </div>

        <div className="shrink-0 text-3xl font-black text-[var(--panel-primary)]">{props.value}</div>
      </div>

      <div className="mt-4 whitespace-pre-line text-sm text-slate-600 dark:text-slate-300">{props.body}</div>
    </Card>
  );
}

export default function UserPanelProfil() {
  const { user, loading: sessionLoading } = useSupabaseSession();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const me = useMemo(() => getMe(), []);
  const localUserId = me?.id ? String(me.id) : "local";

  const sessionDbQuery = useQuery({
    queryKey: ["panel-supabase-session"],
    enabled: !sessionLoading,
    queryFn: async () => {
      try {
        const supabase = getSupabaseClient();
        const { data } = await withTimeout(supabase.auth.getSession(), 8000, "Supabase oturumu alınamadı");
        const sessionUser = data.session?.user ?? null;
        return {
          hasSession: Boolean(sessionUser),
          sessionUserId: sessionUser?.id ? String(sessionUser.id) : null,
          sessionEmail: typeof sessionUser?.email === "string" ? sessionUser.email : null,
        };
      } catch {
        return { hasSession: false, sessionUserId: null, sessionEmail: null };
      }
    },
    staleTime: 5_000,
  });

  const dbSynced = Boolean(user?.id) && Boolean(sessionDbQuery.data?.hasSession) && sessionDbQuery.data?.sessionUserId === user?.id;
  const effectiveEmail = (
    (user?.email ?? "").trim() ||
    (sessionDbQuery.data?.sessionEmail ?? "").trim() ||
    (() => {
      try {
        return (globalThis.localStorage?.getItem("panel_saved_email") ?? "").trim();
      } catch {
        return "";
      }
    })()
  ) as string;

  const photoRef = useRef<HTMLDivElement | null>(null);
  const photoButtonRef = useRef<HTMLButtonElement | null>(null);

  const displayNameInputRef = useRef<HTMLInputElement | null>(null);
  const bioInputRef = useRef<HTMLTextAreaElement | null>(null);
  const birthDateInputRef = useRef<HTMLDivElement | null>(null);
  const birthTimeInputRef = useRef<HTMLInputElement | null>(null);
  const birthPlaceInputRef = useRef<HTMLInputElement | null>(null);

  const [activeTab, setActiveTab] = useState<SettingsTab>("personal");
  const [highlightField, setHighlightField] = useState<ProfileMissingField | null>(null);

  const profileQuery = useQuery({
    queryKey: ["panel-profile", user?.id],
    enabled: !!user?.id && !sessionLoading,
    queryFn: async () => {
      if (!user?.id) throw new Error("Oturum yok");
      try {
        return await ensureMyKarmaProfile(user);
      } catch (error) {
        if (!isAbortLikeError(error)) throw error;
        return {
          ...buildFallbackProfile(user),
          ...(loadLocalProfile(user.id) ?? {}),
        } satisfies KarmaProfileRow;
      }
    },
    retry: (failureCount, error) => !isAbortLikeError(error) && failureCount < 2,
    staleTime: 30_000,
  });

  const statsQuery = useQuery({
    queryKey: ["panel-profile-stats", user?.id],
    enabled: !!user?.id && !sessionLoading,
    queryFn: async () => {
      if (!user?.id) throw new Error("Oturum yok");
      return fetchPanelStats(user);
    },
    staleTime: 10_000,
  });

  const blockedUsersQuery = useQuery({
    queryKey: ["panel-blocked-users", user?.id],
    enabled: !!user?.id && !sessionLoading,
    queryFn: async () => {
      if (!user?.id) return [];
      return listBlockedUsersInDb();
    },
    staleTime: 5_000,
  });

  const progressSummaryQuery = useQuery({
    queryKey: ["progress-summary", user?.id],
    enabled: !!user?.id && !sessionLoading,
    queryFn: fetchProgressSummary,
    staleTime: 8_000,
  });

  const progressTimelineQuery = useQuery({
    queryKey: ["progress-timeline", user?.id],
    enabled: !!user?.id && !sessionLoading,
    queryFn: fetchProgressTimeline,
    staleTime: 8_000,
  });

  const savedProfileRef = useRef<KarmaProfileRow | null>(null);
  const savedExtrasRef = useRef<ProfileExtras | null>(null);
  const savedMatchingPrefsRef = useRef<MatchingPreferencesDraft | null>(null);
  const matchingPrefsHydratedRef = useRef(false);
  const lastPersistedSelectedAddressIdRef = useRef<string | null>(null);
  const extrasHydratedRef = useRef(false);

  const [draft, setDraft] = useState<{
    display_name: string;
    email: string;
    birth_date: string;
    bio: string;
    match_enabled: boolean;
    gender: "kadin" | "erkek" | "non_binary" | "belirtmek_istemiyorum" | "";
    photo_url: string;
  } | null>(null);

  useEffect(() => {
    if (draft) return;
    if (sessionLoading) return;

    if (user?.id) {
      const local = loadLocalProfile(user.id);
      const fallback = buildFallbackProfile(user);

      setDraft({
        display_name: fallback.display_name,
        email: effectiveEmail,
        birth_date: typeof fallback.birth_date === "string" ? fallback.birth_date : "",
        bio: typeof local?.bio === "string" ? local.bio : typeof fallback.bio === "string" ? fallback.bio : "",
        match_enabled: typeof local?.match_enabled === "boolean" ? local.match_enabled : Boolean(fallback.match_enabled),
        gender: normalizeGender(local?.gender ?? fallback.gender) ?? "",
        photo_url:
          typeof local?.photo_url === "string" ? local.photo_url : typeof fallback.photo_url === "string" ? fallback.photo_url : "",
      });

      return;
    }

    const meBirth = (me as any)?.birthDate as KarmaBirthDate | undefined;
    const meBirthIso = meBirth ? toIsoBirthDate(meBirth) : null;

    setDraft({
      display_name: typeof (me as any)?.displayName === "string" ? (me as any).displayName : "Kullanıcı",
      email: "",
      birth_date: typeof meBirthIso === "string" ? meBirthIso : "",
      bio: typeof (me as any)?.bio === "string" ? (me as any).bio : "",
      match_enabled: true,
      gender: normalizeGender((me as any)?.gender) ?? "",
      photo_url: "",
    });
  }, [draft, effectiveEmail, me, sessionLoading, user?.id]);

  const [extrasDraft, setExtrasDraft] = useState<ProfileExtras>({
    birth_time: "",
    birth_place: "",
    interests: [],
    location_matching: false,
    saved_addresses: [],
    selected_address_id: "",
  });

  const [matchingPreferencesDraft, setMatchingPreferencesDraft] = useState<MatchingPreferencesDraft>(() =>
    buildMatchingPreferencesDraft(me),
  );

  useEffect(() => {
    const ownerId = user?.id ?? localUserId;

    if (!savedExtrasRef.current) {
      const extras = loadExtras(ownerId);
      savedExtrasRef.current = extras;
      setExtrasDraft(extras);
    }

    extrasHydratedRef.current = true;
  }, [localUserId, user?.id]);

  useEffect(() => {
    if (!extrasHydratedRef.current) return;
    const ownerId = user?.id ?? localUserId;
    saveExtras(ownerId, extrasDraft);
  }, [extrasDraft, localUserId, user?.id]);

  useEffect(() => {
    if (matchingPrefsHydratedRef.current) return;
    const next = buildMatchingPreferencesDraft(getMe());
    setMatchingPreferencesDraft(next);
    savedMatchingPrefsRef.current = next;
    matchingPrefsHydratedRef.current = true;
  }, []);

  useEffect(() => {
    if (!profileQuery.data) return;

    if (!savedProfileRef.current) {
      savedProfileRef.current = profileQuery.data;
    }

    setDraft({
      display_name: profileQuery.data.display_name ?? "",
      email: effectiveEmail,
      birth_date: profileQuery.data.birth_date ?? "",
      bio: profileQuery.data.bio ?? "",
      match_enabled: Boolean(profileQuery.data.match_enabled),
      gender: normalizeGender(profileQuery.data.gender) ?? "",
      photo_url: profileQuery.data.photo_url ?? "",
    });

    const ownerId = user?.id ?? localUserId;
    const localExtras = loadExtras(ownerId);

    // Hydrate extras from DB if available; fallback to local extras.
    setExtrasDraft((prev) => {
      const dbSavedAddresses = normalizeSavedAddresses(profileQuery.data?.saved_addresses);
      const savedAddresses = dbSavedAddresses.length ? dbSavedAddresses : localExtras.saved_addresses;
      const selectedAddressIdFromDb = typeof profileQuery.data?.selected_address_id === "string" ? profileQuery.data.selected_address_id : "";
      const selectedAddressId = savedAddresses.some((entry) => entry.id === selectedAddressIdFromDb)
        ? selectedAddressIdFromDb
        : savedAddresses.some((entry) => entry.id === localExtras.selected_address_id)
          ? localExtras.selected_address_id
          : savedAddresses[0]?.id ?? "";

      return {
        ...prev,
        birth_time: typeof profileQuery.data?.birth_time === "string" ? profileQuery.data.birth_time : localExtras.birth_time,
        birth_place: typeof profileQuery.data?.birth_place === "string" ? profileQuery.data.birth_place : localExtras.birth_place,
        interests: Array.isArray(profileQuery.data?.interests)
          ? (profileQuery.data.interests.filter((v) => typeof v === "string") as string[])
          : localExtras.interests,
        location_matching:
          typeof profileQuery.data?.location_matching === "boolean" ? profileQuery.data.location_matching : localExtras.location_matching,
        saved_addresses: savedAddresses,
        selected_address_id: selectedAddressId,
      };
    });

    setMatchingPreferencesDraft((prev) => ({
      ...prev,
      prefMinAge: String(
        Math.max(18, Math.min(99, Number(profileQuery.data?.pref_min_age ?? prev.prefMinAge) || 18)),
      ),
      prefMaxAge: String(
        Math.max(
          Math.max(18, Math.min(99, Number(profileQuery.data?.pref_min_age ?? prev.prefMinAge) || 18)),
          Math.min(99, Number(profileQuery.data?.pref_max_age ?? prev.prefMaxAge) || 99),
        ),
      ),
      prefOnlyMatchEnabled:
        typeof profileQuery.data?.pref_only_match_enabled === "boolean"
          ? profileQuery.data.pref_only_match_enabled
          : prev.prefOnlyMatchEnabled,
      prefPartnerGender: normalizePreferredGender(profileQuery.data?.preferred_gender ?? prev.prefPartnerGender),
      prefDestinyNumbers: Array.isArray(profileQuery.data?.pref_destiny_numbers)
        ? profileQuery.data.pref_destiny_numbers
        : prev.prefDestinyNumbers,
      prefHeartDesireNumbers: Array.isArray(profileQuery.data?.pref_heart_desire_numbers)
        ? profileQuery.data.pref_heart_desire_numbers
        : prev.prefHeartDesireNumbers,
      prefExpressionNumbers: Array.isArray(profileQuery.data?.pref_expression_numbers)
        ? profileQuery.data.pref_expression_numbers
        : prev.prefExpressionNumbers,
      prefKarmicDebtNumbers: Array.isArray(profileQuery.data?.pref_karmic_debt_numbers)
        ? profileQuery.data.pref_karmic_debt_numbers
        : prev.prefKarmicDebtNumbers,
    }));
  }, [effectiveEmail, localUserId, profileQuery.data, user?.id]);

  const birthDate = useMemo(() => parseBirthDateFromRow(draft?.birth_date ?? null), [draft?.birth_date]);

  const profileInput = useMemo(() => {
    if (!draft?.display_name || !birthDate) return null;

    const parts = draft.display_name.trim().split(" ").filter(Boolean);
    const firstName = parts[0] ?? "";
    const lastName = parts.slice(1).join(" ");

    return {
      firstName,
      lastName,
      fullName: draft.display_name,
      email: draft.email || null,
      birthDate,
      birthText: formatBirthText(birthDate),
    };
  }, [birthDate, draft?.display_name, draft?.email]);

  const numerology = useMemo(() => {
    if (!profileInput) return null;
    return computeKarmaNumerology(profileInput.fullName, profileInput.birthDate);
  }, [profileInput]);

  const archetypeProfile = useMemo(() => {
    if (!numerology) return null;
    return computeArchetypeProfile(numerology);
  }, [numerology]);

  const panelElementLabel = useMemo(() => {
    if (!birthDate) return "";
    return getElement(birthDate.year);
  }, [birthDate?.year]);

  const yearForesight = useMemo(() => {
    if (!birthDate) return "";
    return calculateYearForesight(birthDate.day, birthDate.month);
  }, [birthDate?.day, birthDate?.month]);

  const [templateCopy, setTemplateCopy] = useState<null | {
    destiny?: string;
    expression?: string;
    heart_desire?: string;
    personality?: string;
    birth_day?: string;
    element?: string;
    year_foresight?: string;
    karmic_dept?: string;
  }>(null);

  useEffect(() => {
    if (!profileInput) return;

    let cancelled = false;

    void (async () => {
      try {
        const response = await fetch("/api/numeroloji/portrait-insights", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: profileInput.firstName,
            lastName: profileInput.lastName,
            day: profileInput.birthDate.day,
            month: profileInput.birthDate.month,
            year: profileInput.birthDate.year,
            lang: "tr",
          }),
        });

        if (cancelled) return;
        if (!response.ok) return;

        const data = (await response.json().catch(() => null)) as any;
        if (cancelled) return;
        if (!data?.ok) return;

        setTemplateCopy(data?.texts ?? null);
      } catch (err) {
        if (cancelled) return;
        const anyErr = err as any;
        const name = String(anyErr?.name ?? "");
        const msg = String(anyErr?.message ?? "").toLowerCase();
        if (name === "AbortError" || msg.includes("signal is aborted")) return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profileInput?.birthDate.day, profileInput?.birthDate.month, profileInput?.birthDate.year, profileInput?.firstName, profileInput?.lastName]);

  const cards = useMemo(() => {
    if (!numerology) return [];

    const hasMeaningfulText = (value: unknown): value is string => {
      if (typeof value !== "string") return false;
      return value.replace(/[\s\u00a0\u200b\u200c\u200d]+/g, "").length > 0;
    };

    const insights = getProfileInsights(numerology);
    const byId = new Map(insights.map((it) => [it.id, it.body] as const));

    const getInsight = (id: string) => byId.get(id) ?? "";

    const pickCardBody = (templateValue: unknown, insightId: string, fallback: string) => {
      if (hasMeaningfulText(templateValue)) return templateValue;
      const insightValue = getInsight(insightId);
      if (hasMeaningfulText(insightValue)) return insightValue;
      return fallback;
    };

    const lifePath = numerology.lifePathMaster ?? numerology.destinyNumber;

    return [
      {
        label: "Yaşam Yolu",
        value: String(lifePath),
        meta: `Kader sayısı: ${numerology.destinyPath}`,
        body: pickCardBody(templateCopy?.destiny, "destiny", ""),
      },
      {
        label: "İfade Numarası",
        value: String(numerology.expressionNumber),
        body: pickCardBody(templateCopy?.expression, "expression", `İfade numarası: ${numerology.expressionNumber}`),
      },
      {
        label: "Kalp Arzusu",
        value: String(numerology.heartDesireNumber),
        body: pickCardBody(templateCopy?.heart_desire, "heart", ""),
      },
      {
        label: "Kişilik Sayısı",
        value: String(numerology.personalityNumber),
        body: pickCardBody(templateCopy?.personality, "personality", ""),
      },
      {
        label: "Doğum Günü",
        value: String(numerology.birthDayNumber),
        body: pickCardBody(templateCopy?.birth_day, "birthday", ""),
      },
      {
        label: "Element",
        value: panelElementLabel || "—",
        body: pickCardBody(templateCopy?.element, "element", "Doğum yılına göre mevcut dönem element döngüsü."),
      },
      {
        label: "Yıl Öngörü",
        value: yearForesight || "—",
        body: pickCardBody(templateCopy?.year_foresight, "year_foresight", "Yıllık tema ve yönelim."),
      },
      {
        label: "PIN Kodları",
        value: numerology.pinText,
        body: "Doğum tarihinden hesaplanan 9 haneli enerji dizilimi. PIN kodları; ritmini, tekrar eden temalarını ve dengelenmesi gereken alanları gösterir.",
      },
      {
        label: "Karmik Borç",
        value: numerology.karmicCodes.length ? numerology.karmicCodes.join(",") : "—",
        body: pickCardBody(templateCopy?.karmic_dept, "karmic_codes", "Karmik borç kodu bulunamadı."),
      },
    ] as const;
  }, [numerology, panelElementLabel, templateCopy, yearForesight]);

  const birthDateParts = useMemo(() => parseBirthDateParts(draft?.birth_date ?? ""), [draft?.birth_date]);
  const birthYearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 100 }, (_, idx) => String(now - idx));
  }, []);
  const birthMonthOptions = useMemo(
    () => [
      { value: "01", label: "Ocak" },
      { value: "02", label: "Şubat" },
      { value: "03", label: "Mart" },
      { value: "04", label: "Nisan" },
      { value: "05", label: "Mayıs" },
      { value: "06", label: "Haziran" },
      { value: "07", label: "Temmuz" },
      { value: "08", label: "Ağustos" },
      { value: "09", label: "Eylül" },
      { value: "10", label: "Ekim" },
      { value: "11", label: "Kasım" },
      { value: "12", label: "Aralık" },
    ],
    [],
  );
  const birthDayOptions = useMemo(() => {
    const fallbackYear = Number(birthDateParts.year) || 2000;
    const fallbackMonth = Number(birthDateParts.month) || 1;
    const dayCount = getDaysInMonth(fallbackYear, fallbackMonth);
    return Array.from({ length: dayCount }, (_, idx) => String(idx + 1).padStart(2, "0"));
  }, [birthDateParts.month, birthDateParts.year]);

  const completeness = useMemo(() => {
    const total = 7;
    let done = 0;

    if (draft?.display_name.trim()) done += 1;
    if (draft?.birth_date) done += 1;
    if (draft?.bio.trim()) done += 1;
    if (draft?.photo_url.trim()) done += 1;

    if (extrasDraft.birth_time.trim()) done += 1;
    if (extrasDraft.birth_place.trim()) done += 1;
    if (extrasDraft.interests.length > 0) done += 1;

    const percent = Math.max(0, Math.min(100, Math.round((done / total) * 100)));
    return { done, total, percent };
  }, [draft?.bio, draft?.birth_date, draft?.display_name, draft?.photo_url, extrasDraft.birth_place, extrasDraft.birth_time, extrasDraft.interests.length]);

  const missingFields = useMemo<ProfileMissingField[]>(() => {
    if (!draft) return [];

    const out: ProfileMissingField[] = [];

    if (!draft.photo_url.trim()) out.push("photo");
    if (!draft.display_name.trim()) out.push("display_name");
    if (!draft.birth_date) out.push("birth_date");
    if (!draft.bio.trim()) out.push("bio");
    if (!extrasDraft.birth_time.trim()) out.push("birth_time");
    if (!extrasDraft.birth_place.trim()) out.push("birth_place");
    if (extrasDraft.interests.length === 0) out.push("interests");

    return out;
  }, [draft, extrasDraft.birth_place, extrasDraft.birth_time, extrasDraft.interests.length]);

  useEffect(() => {
    if (!highlightField) return;
    const t = window.setTimeout(() => setHighlightField(null), 3000);
    return () => window.clearTimeout(t);
  }, [highlightField]);

  const lastSaveTargetRef = useRef<"db">("db");

  const joinDateLabel = useMemo(() => {
    const createdAt = profileQuery.data?.created_at;
    if (!createdAt) return "—";
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("tr-TR", { month: "long", year: "numeric" }).format(d);
  }, [profileQuery.data?.created_at]);

  const progressSummary = progressSummaryQuery.data as
    | {
        totals?: { total_xp?: number };
        phase?: {
          rank?: { current?: { label?: string }; next?: { label?: string } | null; progress?: number; xpToNext?: number };
          aura?: { label?: string; progressPercent?: number };
          streak?: { current?: number };
          badges?: Array<{ key: string; label: string }>;
        };
        rankCatalog?: Array<{ key: string; label: string; imageUrl?: string | null }>;
        auraCatalog?: Array<{ key: string; label: string; imageUrl?: string | null }>;
        badgeCatalog?: Array<{ key: string; label: string; imageUrl?: string | null }>;
      }
    | null;

  const progressTimeline = progressTimelineQuery.data as
    | {
        items?: Array<{ id: string; label?: string; source?: string; awardedXp?: number; occurredAt?: string }>;
      }
    | null;

  const progressBadges = Array.isArray(progressSummary?.phase?.badges) ? progressSummary.phase.badges : [];
  const progressEvents = Array.isArray(progressTimeline?.items) ? progressTimeline.items.slice(0, 5) : [];

  const profileNameForNarrative = String(draft?.display_name ?? "").trim() || "Bu kişi";
  const numerologyPublicCards = cards
    .filter((item) => item.label === "Yaşam Yolu" || item.label === "İfade Numarası" || item.label === "Kalp Arzusu")
    .map((item) => ({
      ...item,
      lead: `${profileNameForNarrative} için ${item.label.toLocaleLowerCase("tr-TR")} ${item.value} olarak hesaplanır.`,
    }));

  const auraCatalog = Array.isArray(progressSummary?.auraCatalog) ? progressSummary.auraCatalog : [];
  const rankCatalog = Array.isArray(progressSummary?.rankCatalog) ? progressSummary.rankCatalog : [];
  const badgeCatalog = Array.isArray(progressSummary?.badgeCatalog) ? progressSummary.badgeCatalog : [];
  const badgeImageByKey = new Map<string, string>();
  for (const item of [...auraCatalog, ...rankCatalog, ...badgeCatalog]) {
    const key = String(item?.key ?? "").trim();
    const imageUrl = String(item?.imageUrl ?? "").trim();
    if (!key || !imageUrl) continue;
    badgeImageByKey.set(key, imageUrl);
  }

  const journeyBadgeStrip = [
    {
      key: `aura:${String(progressSummary?.phase?.aura?.label ?? "Aura")}`,
      label: String(progressSummary?.phase?.aura?.label ?? "Aura").trim() || "Aura",
      imageUrl: badgeImageByKey.get(String(progressSummary?.phase?.aura?.label ?? "").toLocaleLowerCase("tr-TR").replace(/\s+/g, "_")) ?? "",
    },
    {
      key: `rank:${String(progressSummary?.phase?.rank?.current?.label ?? "Unvan")}`,
      label: String(progressSummary?.phase?.rank?.current?.label ?? "Unvan").trim() || "Unvan",
      imageUrl: badgeImageByKey.get(String(progressSummary?.phase?.rank?.current?.label ?? "").toLocaleLowerCase("tr-TR").replace(/\s+/g, "_")) ?? "",
    },
    ...progressBadges.slice(0, 4).map((badge) => ({
      key: `badge:${badge.key}`,
      label: badge.label,
      imageUrl: badgeImageByKey.get(badge.key) ?? "",
    })),
  ].filter((badge) => badge.label);

  const unblockUserMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      return unblockUserInDb(targetUserId);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["panel-blocked-users", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["karma-interactions", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["karma-likes-and-matches", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["karma-candidates", user?.id] }),
      ]);
      toast({ title: "Engel kaldırıldı" });
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Engel kaldırılamadı";
      toast({ title: "Engel kaldırılamadı", description: message, variant: "destructive" });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      return withTimeout(
        (async () => {
          if (!draft) throw new Error("Profil henüz hazır değil");

          const displayName = draft.display_name.trim();
          if (!displayName) throw new Error("Ad soyad gerekli.");

          // DB-only mode: require an active, verified Supabase session.
          if (!dbSynced) {
            throw new Error("Kaydetmek için Supabase oturumu gerekli. Lütfen tekrar giriş yapın.");
          }

          const supabase = getSupabaseClient();

          const { data: sessionData } = await withTimeout(supabase.auth.getSession(), 8000, "Supabase oturumu alınamadı");
          const sessionUser = sessionData.session?.user;

          if (!sessionUser || String(sessionUser.id) !== String(user?.id)) {
            throw new Error("Supabase oturumu geçersiz. Lütfen tekrar giriş yapın.");
          }

          let persistedSelectedAddressId = extrasDraft.selected_address_id || null;
          let serializeJsonForTextColumns = false;

          const buildUpdatePatch = () => ({
            display_name: displayName,
            birth_date: draft.birth_date ? draft.birth_date : null,
            bio: draft.bio.trim(),
            match_enabled: Boolean(draft.match_enabled),
            gender: normalizeGender(draft.gender) ?? null,
            preferred_gender: matchingPreferencesNormalized.prefPartnerGender,
            pref_min_age: matchingPreferencesNormalized.prefMinAge,
            pref_max_age: matchingPreferencesNormalized.prefMaxAge,
            pref_only_match_enabled: matchingPreferencesNormalized.prefOnlyMatchEnabled,
            pref_destiny_numbers: matchingPreferencesNormalized.prefDestinyNumbers ?? [],
            pref_heart_desire_numbers: matchingPreferencesNormalized.prefHeartDesireNumbers ?? [],
            pref_expression_numbers: matchingPreferencesNormalized.prefExpressionNumbers ?? [],
            pref_karmic_debt_numbers: matchingPreferencesNormalized.prefKarmicDebtNumbers ?? [],
            photo_url: draft.photo_url.trim(),
            // Extras (requires DB columns):
            birth_time: extrasDraft.birth_time.trim() ? extrasDraft.birth_time.trim() : null,
            birth_place: extrasDraft.birth_place.trim() ? extrasDraft.birth_place.trim() : null,
            interests: serializeJsonForTextColumns ? JSON.stringify(extrasDraft.interests ?? []) : extrasDraft.interests,
            location_matching: Boolean(extrasDraft.location_matching),
            saved_addresses: serializeJsonForTextColumns ? JSON.stringify(extrasDraft.saved_addresses ?? []) : extrasDraft.saved_addresses,
            selected_address_id: persistedSelectedAddressId,
          });

          const runUpdate = () => supabase.from("karma_profiles").update(buildUpdatePatch() as any).eq("id", sessionUser.id);

          let { error } = await withTimeout(runUpdate(), 10_000, "Veritabanına kaydedilemedi (zaman aşımı)");
          if (error) {
            let msg = String((error as any)?.message ?? "");
            let lowerMsg = msg.toLowerCase();
            const missingExtrasColumn =
              (msg.includes("birth_time") || msg.includes("birth_place") || msg.includes("interests") || msg.includes("location_matching") || msg.includes("saved_addresses") || msg.includes("selected_address_id") || msg.includes("gender") || msg.includes("preferred_gender") || msg.includes("pref_min_age") || msg.includes("pref_max_age") || msg.includes("pref_only_match_enabled") || msg.includes("pref_destiny_numbers") || msg.includes("pref_heart_desire_numbers") || msg.includes("pref_expression_numbers") || msg.includes("pref_karmic_debt_numbers")) &&
              (lowerMsg.includes("column") || lowerMsg.includes("schema cache") || lowerMsg.includes("could not find"));

            if (missingExtrasColumn) {
              throw new Error(
                "Veritabanı şeması eksik: karma_profiles tablosuna birth_time, birth_place, interests, location_matching, saved_addresses, selected_address_id, gender, preferred_gender, pref_min_age, pref_max_age, pref_only_match_enabled, pref_destiny_numbers, pref_heart_desire_numbers, pref_expression_numbers, pref_karmic_debt_numbers kolonları eklenmeli.",
              );
            }

            const selectedIdTypeError = msg.includes("selected_address_id") && (lowerMsg.includes("uuid") || lowerMsg.includes("invalid input syntax"));
            if (selectedIdTypeError) {
              persistedSelectedAddressId = null;
              const retry = await withTimeout(runUpdate(), 10_000, "Veritabanına kaydedilemedi (zaman aşımı)");
              error = retry.error ?? null;
              msg = String((error as any)?.message ?? "");
              lowerMsg = msg.toLowerCase();
            }

            const jsonTextMismatch =
              (msg.includes("saved_addresses") || msg.includes("interests")) &&
              (lowerMsg.includes("type text") ||
                lowerMsg.includes("character varying") ||
                lowerMsg.includes("expression is of type json") ||
                lowerMsg.includes("expression is of type jsonb"));

            if (error && jsonTextMismatch) {
              serializeJsonForTextColumns = true;
              const retry = await withTimeout(runUpdate(), 10_000, "Veritabanına kaydedilemedi (zaman aşımı)");
              error = retry.error ?? null;
              msg = String((error as any)?.message ?? "");
            }

            if (error) {
              const detail = String((error as any)?.details ?? "");
              const hint = String((error as any)?.hint ?? "");
              const code = String((error as any)?.code ?? "");
              const diagnostic = [msg, detail, hint, code].filter(Boolean).join(" | ");
              throw new Error(diagnostic || "Kaydedilemedi");
            }
          }

          lastPersistedSelectedAddressIdRef.current = persistedSelectedAddressId;

          lastSaveTargetRef.current = "db";

          // Cache base profile locally for fast UI hydration.
          saveLocalProfile(sessionUser.id, {
            display_name: displayName,
            birth_date: draft.birth_date ? draft.birth_date : null,
            bio: draft.bio.trim(),
            match_enabled: Boolean(draft.match_enabled),
            gender: normalizeGender(draft.gender) ?? null,
            preferred_gender: matchingPreferencesNormalized.prefPartnerGender,
            photo_url: draft.photo_url.trim(),
          });

          persistMatchingPreferences();
        })(),
        15_000,
        "Kaydetme işlemi zaman aşımına uğradı. Lütfen tekrar deneyin.",
      );
    },
    onSuccess: async () => {
      const ownerId = user?.id ?? localUserId;
      const normalizedExtras: ProfileExtras = {
        ...extrasDraft,
        saved_addresses: extrasDraft.saved_addresses.map((entry) => ({
          ...entry,
          identity_number: String(entry.identity_number ?? "").replace(/[^0-9]/g, "").slice(0, 11),
        })),
        selected_address_id: lastPersistedSelectedAddressIdRef.current,
      };

      saveExtras(ownerId, normalizedExtras);
      savedExtrasRef.current = normalizedExtras;
      savedProfileRef.current = null;

      await queryClient.invalidateQueries({ queryKey: ["panel-profile", user?.id] });
      await queryClient.invalidateQueries({ queryKey: ["panel-profile-stats", user?.id] });

      toast({
        title: "Değişiklikler kaydedildi",
        description: "Profil ve kayıtlı adresler güncellendi.",
      });
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Kaydedilemedi";
      const hint = message.includes("Veritabanı şeması eksik")
        ? "Supabase SQL Editor'da gerekli kolonları ekledikten sonra tekrar deneyin."
        : message;
      toast({ title: "Kaydedilemedi", description: hint, variant: "destructive" });
    },
  });

  const resetDraft = () => {
    const base = savedProfileRef.current ?? profileQuery.data;
    if (base) {
      setDraft({
        display_name: base.display_name ?? "",
        email: effectiveEmail,
        birth_date: base.birth_date ?? "",
        bio: base.bio ?? "",
        match_enabled: Boolean(base.match_enabled),
        gender: normalizeGender(base.gender) ?? "",
        photo_url: base.photo_url ?? "",
      });
    }

    const extras = savedExtrasRef.current;
    if (extras) {
      setExtrasDraft(extras);
    }

    const savedMatchingPrefs = savedMatchingPrefsRef.current;
    if (savedMatchingPrefs) {
      setMatchingPreferencesDraft(savedMatchingPrefs);
    } else {
      setMatchingPreferencesDraft(buildMatchingPreferencesDraft(getMe()));
    }
  };

  const setBirthDatePart = (part: "day" | "month" | "year", value: string) => {
    setDraft((prev) => {
      if (!prev) return prev;

      const nextParts = { ...parseBirthDateParts(prev.birth_date), [part]: value };
      const maxDay = getDaysInMonth(Number(nextParts.year) || 2000, Number(nextParts.month) || 1);
      if (nextParts.day && Number(nextParts.day) > maxDay) {
        nextParts.day = String(maxDay).padStart(2, "0");
      }

      return {
        ...prev,
        birth_date: buildIsoBirthDateFromParts(nextParts),
      };
    });

    if (highlightField === "birth_date") setHighlightField(null);
  };

  const changeTab = (tab: SettingsTab) => {
    setActiveTab(tab);
  };

  const highlightAndFocus = (field: ProfileMissingField) => {
    setHighlightField(field);

    window.setTimeout(() => {
      const el =
        field === "photo"
          ? photoButtonRef.current
          : field === "display_name"
            ? displayNameInputRef.current
            : field === "bio"
              ? bioInputRef.current
              : field === "birth_date"
                ? birthDateInputRef.current
                : field === "birth_time"
                  ? birthTimeInputRef.current
                  : field === "birth_place"
                    ? birthPlaceInputRef.current
                    : null;

      el?.focus?.();
    }, 450);
  };

  const onCompleteMissing = () => {
    if (!draft) {
      toast({ title: "Profil yükleniyor", description: "Lütfen birkaç saniye sonra tekrar deneyin." });
      return;
    }

    const first = missingFields[0] ?? null;

    if (!first) {
      toast({ title: "Profil tamam", description: "Eksik bilgi bulunmuyor." });
      return;
    }

    if (first === "interests") {
      setActiveTab("interests");
      setHighlightField("interests");
      return;
    }

    setActiveTab("personal");
    highlightAndFocus(first);
  };

  const interestSuggestions = useMemo(
    () => ["Yoga", "Astroloji", "Seyahat", "Meditasyon", "Sanat", "Nefes", "Ritüeller", "Tarot"],
    [],
  );

  const matchingPreferencesNormalized = useMemo(() => {
    const minAge = Math.max(18, Math.min(99, Number(matchingPreferencesDraft.prefMinAge) || 18));
    const maxAge = Math.max(minAge, Math.min(99, Number(matchingPreferencesDraft.prefMaxAge) || 99));

    return {
      prefMinAge: minAge,
      prefMaxAge: maxAge,
      prefOnlyMatchEnabled: Boolean(matchingPreferencesDraft.prefOnlyMatchEnabled),
      prefOnlySimilarStatus: Boolean(matchingPreferencesDraft.prefOnlySimilarStatus),
      prefPartnerGender: normalizePreferredGender(matchingPreferencesDraft.prefPartnerGender),
      prefDestinyNumbers: normalizePreferenceDigits(matchingPreferencesDraft.prefDestinyNumbers),
      prefHeartDesireNumbers: normalizePreferenceDigits(matchingPreferencesDraft.prefHeartDesireNumbers),
      prefExpressionNumbers: normalizePreferenceDigits(matchingPreferencesDraft.prefExpressionNumbers),
      prefKarmicDebtNumbers: normalizePreferenceDigits(matchingPreferencesDraft.prefKarmicDebtNumbers),
    };
  }, [matchingPreferencesDraft]);

  const persistMatchingPreferences = () => {
    const meNow = getMe();
    updateUser({
      ...meNow,
      ...matchingPreferencesNormalized,
    });

    savedMatchingPrefsRef.current = {
      prefMinAge: String(matchingPreferencesNormalized.prefMinAge),
      prefMaxAge: String(matchingPreferencesNormalized.prefMaxAge),
      prefOnlyMatchEnabled: matchingPreferencesNormalized.prefOnlyMatchEnabled,
      prefOnlySimilarStatus: matchingPreferencesNormalized.prefOnlySimilarStatus,
      prefPartnerGender: matchingPreferencesNormalized.prefPartnerGender,
      prefDestinyNumbers: matchingPreferencesNormalized.prefDestinyNumbers ?? [],
      prefHeartDesireNumbers: matchingPreferencesNormalized.prefHeartDesireNumbers ?? [],
      prefExpressionNumbers: matchingPreferencesNormalized.prefExpressionNumbers ?? [],
      prefKarmicDebtNumbers: matchingPreferencesNormalized.prefKarmicDebtNumbers ?? [],
    };
  };

  const canSave = Boolean(draft?.display_name?.trim()) && !saveMutation.isPending && !sessionLoading && dbSynced;

  const profilePhotoUrl = draft?.photo_url?.trim() || "/placeholder.svg";

  const readAccessTokenFromStorage = () => {
    try {
      const { directUrl } = getSupabaseConfig();
      const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
      if (!storageKey) return "";
      const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
      const parsed = raw ? (JSON.parse(raw) as any) : null;
      return typeof parsed?.access_token === "string" ? parsed.access_token : "";
    } catch {
      return "";
    }
  };

  const onPickPhoto = () => {
    if (!dbSynced) {
      toast({ title: "Fotoğraf yüklenemedi", description: "Lütfen giriş yapın.", variant: "destructive" });
      return;
    }
    fileInputRef.current?.click();
  };

  const onUploadPhoto = async (file: File) => {
    if (!dbSynced) throw new Error("Oturum yok");

    if (file.size > 6 * 1024 * 1024) {
      throw new Error("Dosya çok büyük (max 6MB)");
    }

    const token = readAccessTokenFromStorage();
    if (!token) throw new Error("Oturum anahtarı bulunamadı");

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Dosya okunamadı"));
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.readAsDataURL(file);
    });

    const idx = dataUrl.indexOf(",");
    const base64 = idx >= 0 ? dataUrl.slice(idx + 1) : "";
    if (!base64) throw new Error("Dosya verisi okunamadı");

    const res = await fetch("/api/profile/photo", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        contentType: file.type || "image/jpeg",
        dataBase64: base64,
      }),
    });

    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok || !json?.ok) {
      const msg = typeof json?.error === "string" ? json.error : "Fotoğraf yüklenemedi";
      throw new Error(msg);
    }

    const url = typeof json?.photo_url === "string" ? json.photo_url : "";
    if (!url) throw new Error("Fotoğraf URL alınamadı");

    setDraft((prev) => (prev ? { ...prev, photo_url: url } : prev));

    savedProfileRef.current = null;
    await queryClient.invalidateQueries({ queryKey: ["panel-profile", user?.id] });

    toast({ title: "Profil fotoğrafı güncellendi", description: "Veritabanına kaydedildi." });
  };

  const lifePathBadge = numerology ? String(numerology.lifePathMaster ?? numerology.destinyNumber) : "—";
  const archetypeBadge = archetypeProfile?.archetype?.name ?? "—";

  return (
    <div className="mx-auto w-full max-w-5xl px-0 pb-28 sm:pb-8">
      <header className="relative mb-6 sm:mb-10">
        <div className="flex flex-col items-center text-center">
          <div ref={photoRef} className="relative mb-6">
            <div
              className={cn(
                "flex size-28 items-center justify-center overflow-hidden rounded-full bg-slate-200 ring-4 shadow-xl sm:size-32",
                highlightField === "photo" ? "ring-amber-300" : "ring-white",
              )}
            >
              {draft?.photo_url?.trim() ? (
                <img src={profilePhotoUrl} alt="Profil fotoğrafı" className="h-full w-full object-cover" />
              ) : (
                <UserIcon className="h-12 w-12 text-slate-400 sm:h-14 sm:w-14" />
              )}
            </div>

            <button
              ref={photoButtonRef}
              type="button"
              onClick={onPickPhoto}
              disabled={photoUploading}
              className="absolute bottom-0 right-0 rounded-full bg-[var(--panel-primary)] p-2 text-white shadow-lg transition-transform hover:scale-105 disabled:opacity-60"
              aria-label="Profil fotoğrafı değiştir"
            >
              <Camera className="h-4 w-4" />
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.currentTarget.files?.[0] ?? null;
                // allow selecting same file again
                e.currentTarget.value = "";
                if (!f) return;
                setPhotoUploading(true);
                void onUploadPhoto(f)
                  .catch((err) => {
                    const msg = err instanceof Error ? err.message : "Fotoğraf yüklenemedi";
                    toast({ title: "Fotoğraf yüklenemedi", description: msg, variant: "destructive" });
                  })
                  .finally(() => setPhotoUploading(false));
              }}
            />
          </div>

          <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white sm:text-3xl">
            {draft?.display_name?.trim() ? draft.display_name : "Profil"}
          </h1>


          <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
            <span className="flex items-center gap-2 rounded-full bg-[var(--panel-primary)]/10 px-4 py-1.5 text-sm font-semibold text-[var(--panel-primary)]">
              <span className="text-base">✦</span>
              Yaşam Yolu: {lifePathBadge}
            </span>

            <span className="rounded-full border border-pink-100 bg-pink-50 px-4 py-1.5 text-sm font-semibold text-pink-600">
              {archetypeBadge}
            </span>
          </div>

          {profileInput?.email || profileInput?.birthText ? (
            <div className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              {profileInput?.email ? profileInput.email : null}
              {profileInput?.email && profileInput?.birthText ? " • " : null}
              {profileInput?.birthText ? `Doğum: ${profileInput.birthText}` : null}
            </div>
          ) : null}
        </div>
      </header>

      <nav className="mb-6 overflow-x-auto pb-1 sm:mb-10">
        <div className="flex w-max min-w-full gap-2 px-1 sm:gap-3 sm:px-2">
          {[
            { key: "personal", label: "Kişisel Bilgiler" },
            { key: "addresses", label: "Adresler" },
            { key: "numerology", label: "Numeroloji Tercihleri" },
            { key: "matching", label: "Eşleşme Tercihleri" },
            { key: "interests", label: "İlgi Alanları" },
            { key: "security", label: "Güvenlik" },
          ].map(
            (tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => changeTab(tab.key as SettingsTab)}
                className={cn(
                  "rounded-full border px-3 py-2 text-xs font-semibold transition-all sm:px-4 sm:text-sm",
                  activeTab === tab.key
                    ? "border-[var(--panel-primary)] bg-[var(--panel-primary)]/10 text-[var(--panel-primary)]"
                    : "border-slate-200 text-slate-700 dark:border-white/10 dark:text-slate-300 hover:border-slate-300 hover:text-slate-900 dark:hover:text-slate-100",
                )}
              >
                {tab.label}
              </button>
            ),
          )}
        </div>
      </nav>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        <div className="space-y-8 lg:col-span-2">
          <section className={cn(activeTab === "personal" || activeTab === "addresses" ? "block" : "hidden")}>
            <Card className="rounded-3xl border-slate-100 bg-white p-4 shadow-sm sm:p-6 lg:p-8 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
              <div className="mb-5 flex items-center gap-2 sm:mb-8">
                <UserIcon className="h-5 w-5 text-[var(--panel-primary)]" />
                <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{activeTab === "addresses" ? "Adresler" : "Kişisel Bilgiler"}</h2>
              </div>

              {!draft ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Profil yükleniyor...
                </div>
              ) : (
                <div className="space-y-6">
                  {activeTab !== "addresses" ? (
                    <>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">Ad Soyad</label>
                      <Input
                        ref={displayNameInputRef}
                        value={draft.display_name}
                        onChange={(e) => {
                          setDraft((prev) => (prev ? { ...prev, display_name: e.target.value } : prev));
                          if (highlightField === "display_name") setHighlightField(null);
                        }}
                        className={cn(
                          "h-12 rounded-2xl border border-slate-300 bg-white px-4 text-slate-900 shadow-sm focus-visible:border-[var(--panel-primary)] focus-visible:ring-[var(--panel-primary)]/25",
                          highlightField === "display_name" && "ring-2 ring-amber-400 ring-offset-2 ring-offset-white",
                        )}
                      />
                    </div>

                    <div>
                    <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">E-posta</label>
                    <Input
                      value={draft.email || "—"}
                      readOnly
                      className="h-12 cursor-not-allowed rounded-2xl border border-slate-200 bg-slate-100 px-4 text-slate-600"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">Cinsiyet</label>
                    <select
                      value={draft.gender}
                      onChange={(e) => setDraft((prev) => (prev ? { ...prev, gender: e.target.value as typeof prev.gender } : prev))}
                      className="h-12 w-full appearance-none rounded-2xl border border-slate-300 bg-white px-3 pr-7 text-left text-sm text-slate-900 shadow-sm outline-none focus:border-[var(--panel-primary)]"
                    >
                      <option value="">Seçiniz</option>
                      <option value="kadin">Kadın</option>
                      <option value="erkek">Erkek</option>
                      <option value="non_binary">Non-binary</option>
                      <option value="belirtmek_istemiyorum">Belirtmek istemiyorum</option>
                    </select>
                  </div>
                </div>

                  <div>
                    <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">Hakkımda</label>
                    <Textarea
                      ref={bioInputRef}
                      value={draft.bio}
                      onChange={(e) => {
                        setDraft((prev) => (prev ? { ...prev, bio: e.target.value } : prev));
                        if (highlightField === "bio") setHighlightField(null);
                      }}
                      rows={4}
                      placeholder="Numerolojik yolculuğunuzdan ve kendinizden bahsedin..."
                      className={cn(
                        "min-h-[120px] rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 shadow-sm focus-visible:border-[var(--panel-primary)] focus-visible:ring-[var(--panel-primary)]/25",
                        highlightField === "bio" && "ring-2 ring-amber-400 ring-offset-2 ring-offset-white",
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-[1.65fr_0.7fr_1fr]">
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">Doğum Tarihi</label>
                      <div
                        ref={birthDateInputRef}
                        tabIndex={-1}
                        className={cn(
                          "grid grid-cols-[0.8fr_1.45fr_1.2fr] gap-1.5 rounded-2xl bg-transparent p-0",
                          highlightField === "birth_date" && "ring-2 ring-amber-400 ring-offset-2 ring-offset-white",
                        )}
                      >
                        <div className="relative">
                          <select
                            value={birthDateParts.day}
                            onChange={(e) => setBirthDatePart("day", e.target.value)}
                            className={cn(
                              "h-12 w-full appearance-none rounded-2xl border border-slate-300 bg-white px-3 pr-7 text-left text-sm text-slate-900 shadow-sm outline-none focus:border-[var(--panel-primary)]",
                              birthDateParts.day ? "font-medium" : "font-normal text-slate-500",
                            )}
                          >
                            <option value="">Gün</option>
                            {birthDayOptions.map((day) => (
                              <option key={day} value={day}>
                                {Number(day)}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                        </div>

                        <div className="relative">
                          <select
                            value={birthDateParts.month}
                            onChange={(e) => setBirthDatePart("month", e.target.value)}
                            className={cn(
                              "h-12 w-full appearance-none rounded-2xl border border-slate-300 bg-white px-3 pr-7 text-left text-sm text-slate-900 shadow-sm outline-none focus:border-[var(--panel-primary)]",
                              birthDateParts.month ? "font-medium" : "font-normal text-slate-500",
                            )}
                          >
                            <option value="">Ay</option>
                            {birthMonthOptions.map((month) => (
                              <option key={month.value} value={month.value}>
                                {month.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                        </div>

                        <div className="relative">
                          <select
                            value={birthDateParts.year}
                            onChange={(e) => setBirthDatePart("year", e.target.value)}
                            className={cn(
                              "h-12 w-full appearance-none rounded-2xl border border-slate-300 bg-white px-3 pr-7 text-left text-sm text-slate-900 shadow-sm outline-none focus:border-[var(--panel-primary)]",
                              birthDateParts.year ? "font-medium" : "font-normal text-slate-500",
                            )}
                          >
                            <option value="">Yıl</option>
                            {birthYearOptions.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">Doğum Saati</label>
                      <Input
                        ref={birthTimeInputRef}
                        type="time"
                        value={extrasDraft.birth_time}
                        onChange={(e) => {
                          setExtrasDraft((prev) => ({ ...prev, birth_time: e.target.value }));
                          if (highlightField === "birth_time") setHighlightField(null);
                        }}
                        className={cn(
                          "h-12 rounded-2xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus-visible:border-[var(--panel-primary)] focus-visible:ring-[var(--panel-primary)]/25",
                          highlightField === "birth_time" && "ring-2 ring-amber-400 ring-offset-2 ring-offset-white",
                        )}
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">Doğum Yeri</label>
                      <Input
                        ref={birthPlaceInputRef}
                        value={extrasDraft.birth_place}
                        onChange={(e) => {
                          setExtrasDraft((prev) => ({ ...prev, birth_place: e.target.value }));
                          if (highlightField === "birth_place") setHighlightField(null);
                        }}
                        placeholder="Şehir"
                        className={cn(
                          "h-12 rounded-2xl border border-slate-300 bg-white px-4 text-slate-900 shadow-sm focus-visible:border-[var(--panel-primary)] focus-visible:ring-[var(--panel-primary)]/25",
                          highlightField === "birth_place" && "ring-2 ring-amber-400 ring-offset-2 ring-offset-white",
                        )}
                      />
                    </div>
                  </div>

                  </>
                  ) : null}

                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-white/10 dark:bg-white/5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Kayıtlı Adresler</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">Ev / İş yeri adreslerini kaydedip ödeme sırasında tek tıkla seçebilirsiniz.</div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-9 rounded-xl border-[var(--panel-primary)]/20 text-[var(--panel-primary)]"
                        onClick={() => {
                          const next = createEmptySavedAddress();
                          setExtrasDraft((prev) => ({
                            ...prev,
                            saved_addresses: [...prev.saved_addresses, next],
                            selected_address_id: next.id,
                          }));
                        }}
                      >
                        Adres Ekle
                      </Button>
                    </div>

                    {extrasDraft.saved_addresses.length ? (
                      <div className="mt-4 space-y-4">
                        {extrasDraft.saved_addresses.map((entry, index) => (
                          <div key={entry.id} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950/30">
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Adres {index + 1}</div>
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1 text-xs text-slate-500">
                                  <input
                                    type="radio"
                                    name="selected-address"
                                    checked={extrasDraft.selected_address_id === entry.id}
                                    onChange={() => setExtrasDraft((prev) => ({ ...prev, selected_address_id: entry.id }))}
                                  />
                                  Varsayılan
                                </label>
                                <button
                                  type="button"
                                  className="text-xs font-semibold text-rose-500 hover:text-rose-600"
                                  onClick={() => {
                                    setExtrasDraft((prev) => {
                                      const nextAddresses = prev.saved_addresses.filter((it) => it.id !== entry.id);
                                      const nextSelected =
                                        prev.selected_address_id === entry.id ? (nextAddresses[0]?.id ?? "") : prev.selected_address_id;
                                      return { ...prev, saved_addresses: nextAddresses, selected_address_id: nextSelected };
                                    });
                                  }}
                                >
                                  Sil
                                </button>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <select
                                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-[var(--panel-primary)]"
                                value={entry.label}
                                onChange={(e) => {
                                  const nextLabel = normalizeSavedAddressLabel(e.target.value);
                                  setExtrasDraft((prev) => ({
                                    ...prev,
                                    saved_addresses: prev.saved_addresses.map((it) => (it.id === entry.id ? { ...it, label: nextLabel } : it)),
                                  }));
                                }}
                              >
                                <option value="ev">Ev</option>
                                <option value="is">İş Yeri</option>
                                <option value="diger">Diğer</option>
                              </select>
                              <Input
                                value={entry.title}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setExtrasDraft((prev) => ({
                                    ...prev,
                                    saved_addresses: prev.saved_addresses.map((it) => (it.id === entry.id ? { ...it, title: val } : it)),
                                  }));
                                }}
                                placeholder="Adres adı (Opsiyonel)"
                                className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                              />
                              <Input
                                value={entry.phone}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setExtrasDraft((prev) => ({
                                    ...prev,
                                    saved_addresses: prev.saved_addresses.map((it) => (it.id === entry.id ? { ...it, phone: val } : it)),
                                  }));
                                }}
                                placeholder="Telefon"
                                className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                              />
                              <Input
                                value={entry.identity_number}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 11);
                                  setExtrasDraft((prev) => ({
                                    ...prev,
                                    saved_addresses: prev.saved_addresses.map((it) => (it.id === entry.id ? { ...it, identity_number: val } : it)),
                                  }));
                                }}
                                placeholder="TCKN (11 hane)"
                                className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                              />
                              <Textarea
                                value={entry.address_line1}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setExtrasDraft((prev) => ({
                                    ...prev,
                                    saved_addresses: prev.saved_addresses.map((it) => (it.id === entry.id ? { ...it, address_line1: val } : it)),
                                  }));
                                }}
                                placeholder="Açık adres"
                                rows={3}
                                className="min-h-[90px] rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 md:col-span-2"
                              />
                              <Input
                                value={entry.city}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setExtrasDraft((prev) => ({
                                    ...prev,
                                    saved_addresses: prev.saved_addresses.map((it) => (it.id === entry.id ? { ...it, city: val } : it)),
                                  }));
                                }}
                                placeholder="Şehir"
                                className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                              />
                              <Input
                                value={entry.district}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setExtrasDraft((prev) => ({
                                    ...prev,
                                    saved_addresses: prev.saved_addresses.map((it) => (it.id === entry.id ? { ...it, district: val } : it)),
                                  }));
                                }}
                                placeholder="İlçe"
                                className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                              />
                              <Input
                                value={entry.postcode}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setExtrasDraft((prev) => ({
                                    ...prev,
                                    saved_addresses: prev.saved_addresses.map((it) => (it.id === entry.id ? { ...it, postcode: val } : it)),
                                  }));
                                }}
                                placeholder="Posta kodu"
                                className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 md:col-span-2"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-3 text-xs text-slate-500">Henüz kayıtlı adres yok.</div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </section>

          <section className={cn(activeTab === "numerology" ? "block" : "hidden")}>
            <Card className="rounded-3xl border-slate-100 bg-white p-4 shadow-sm sm:p-6 lg:p-8 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
              <div className="mb-5 flex items-center gap-2 sm:mb-8">
                <span className="text-[var(--panel-primary)]">✦</span>
                <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Numeroloji Tercihleri</h2>
              </div>

              {numerology ? (
                <div className="space-y-5">
                  <div className="rounded-2xl bg-slate-50/90 p-4 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.10)] dark:bg-white/5">
                    <div className="text-xs font-black uppercase tracking-[0.14em] text-[var(--panel-primary)]">Başkaları Seni Nasıl Okur?</div>
                    <div className="mt-3 grid gap-3 md:grid-cols-3">
                      {numerologyPublicCards.map((card) => (
                        <div key={card.label} className="rounded-xl bg-white p-3 shadow-sm dark:bg-slate-900/60">
                          <div className="text-[11px] font-black uppercase tracking-[0.14em] text-[var(--panel-primary)]">{card.label}</div>
                          <div className="mt-1 text-2xl font-black text-slate-900 dark:text-slate-100">{card.value}</div>
                          <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{card.lead}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {draft?.photo_url?.trim() ? (
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 dark:border-white/10 dark:bg-slate-900/50">
                      <img src={draft.photo_url} alt={profileNameForNarrative} className="h-56 w-full rounded-xl object-cover" loading="lazy" />
                    </div>
                  ) : null}

                  <div className="grid gap-4 md:grid-cols-2">
                    {cards.map((c) => (
                      <PortraitCard key={c.label} label={c.label} value={c.value} meta={"meta" in c ? c.meta : undefined} body={c.body} />
                    ))}
                  </div>

                  {extrasDraft.interests.length ? (
                    <div className="rounded-2xl bg-slate-50 p-4 dark:bg-white/5">
                      <div className="text-xs font-black uppercase tracking-[0.14em] text-[var(--panel-primary)]">İlgi Alanları</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {extrasDraft.interests.map((interest) => (
                          <span key={`numerology-interest-${interest}`} className="rounded-full border border-[var(--panel-primary)]/20 bg-[var(--panel-primary)]/10 px-3 py-1 text-xs font-semibold text-[var(--panel-primary)]">
                            {interest}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="rounded-2xl border border-violet-200/70 bg-white/80 p-4 shadow-sm dark:border-violet-500/20 dark:bg-violet-950/25">
                    <div className="text-xs font-black uppercase tracking-[0.14em] text-[var(--panel-primary)]">Ruhsal Yolculuk Rozetleri</div>
                    <div className="mt-3 flex gap-4 overflow-x-auto pb-1">
                      {journeyBadgeStrip.map((badge) => (
                        <div key={badge.key} className="flex min-w-[92px] shrink-0 flex-col items-center gap-2">
                          <div className="grid h-14 w-14 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-violet-100 to-violet-200 text-[11px] font-black text-[var(--panel-primary)]">
                            {badge.imageUrl ? <img src={badge.imageUrl} alt={badge.label} className="h-full w-full object-cover" loading="lazy" /> : badge.label.slice(0, 2).toLocaleUpperCase("tr-TR")}
                          </div>
                          <span className="max-w-[92px] text-center text-[11px] font-semibold leading-tight text-slate-700 dark:text-slate-200">{badge.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600 dark:bg-white/5 dark:text-slate-300">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div className="font-semibold text-slate-800 dark:text-slate-100">Numeroloji tabanlı keşif</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        Element: {panelElementLabel || "—"} • Yıl öngörü: {yearForesight || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600 dark:bg-white/5 dark:text-slate-300">
                  Numeroloji kartlarını göstermek için doğum tarihi bilgisi gerekli.
                </div>
              )}
            </Card>
          </section>

          <section className={cn(activeTab === "matching" ? "block" : "hidden")}>
            <Card className="rounded-3xl border-slate-100 bg-white p-4 shadow-sm sm:p-6 lg:p-8 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
              <div className="mb-5 flex items-center gap-2 sm:mb-8">
                <span className="text-[var(--panel-primary)]">✦</span>
                <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Eşleşme Tercihleri</h2>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">Minimum Yaş</label>
                  <Input
                    value={matchingPreferencesDraft.prefMinAge}
                    onChange={(e) => setMatchingPreferencesDraft((prev) => ({ ...prev, prefMinAge: e.target.value }))}
                    inputMode="numeric"
                    className="h-12 rounded-2xl border border-slate-300 bg-white px-4 text-slate-900 shadow-sm focus-visible:border-[var(--panel-primary)] focus-visible:ring-[var(--panel-primary)]/25"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">Maksimum Yaş</label>
                  <Input
                    value={matchingPreferencesDraft.prefMaxAge}
                    onChange={(e) => setMatchingPreferencesDraft((prev) => ({ ...prev, prefMaxAge: e.target.value }))}
                    inputMode="numeric"
                    className="h-12 rounded-2xl border border-slate-300 bg-white px-4 text-slate-900 shadow-sm focus-visible:border-[var(--panel-primary)] focus-visible:ring-[var(--panel-primary)]/25"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">Eşleşmek İstediğim Cinsiyet</label>
                <select
                  value={matchingPreferencesDraft.prefPartnerGender}
                  onChange={(e) =>
                    setMatchingPreferencesDraft((prev) => ({ ...prev, prefPartnerGender: normalizePreferredGender(e.target.value) }))
                  }
                  className="h-12 w-full appearance-none rounded-2xl border border-slate-300 bg-white px-3 pr-7 text-left text-sm text-slate-900 shadow-sm outline-none focus:border-[var(--panel-primary)]"
                >
                  <option value="herkes">Herkes</option>
                  <option value="kadin">Kadın</option>
                  <option value="erkek">Erkek</option>
                  <option value="non_binary">Non-binary</option>
                </select>
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between gap-6 rounded-2xl border border-slate-200 p-4 dark:border-white/10">
                  <div>
                    <p className="font-medium text-slate-800 dark:text-slate-100">Sadece Eşleşmeye Açık Profiller</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Kapalı profiller keşifte görünmez.</p>
                  </div>
                  <Switch
                    checked={matchingPreferencesDraft.prefOnlyMatchEnabled}
                    onCheckedChange={(v) => setMatchingPreferencesDraft((prev) => ({ ...prev, prefOnlyMatchEnabled: Boolean(v) }))}
                  />
                </div>

                <div className="flex items-center justify-between gap-6 rounded-2xl border border-slate-200 p-4 dark:border-white/10">
                  <div>
                    <p className="font-medium text-slate-800 dark:text-slate-100">Statü Filtresi</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Açıkken benzer seviyedeki profilleri öne çıkarır.</p>
                  </div>
                  <Switch
                    checked={matchingPreferencesDraft.prefOnlySimilarStatus}
                    onCheckedChange={(v) => setMatchingPreferencesDraft((prev) => ({ ...prev, prefOnlySimilarStatus: Boolean(v) }))}
                  />
                </div>

                <div className="flex items-center justify-between gap-6 rounded-2xl border border-slate-200 p-4 dark:border-white/10">
                  <div>
                    <p className="font-medium text-slate-800 dark:text-slate-100">Profil Görünürlüğü</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Profilinizin diğer üyeler tarafından bulunabilirliği.</p>
                  </div>
                  <Switch
                    checked={Boolean(draft?.match_enabled)}
                    onCheckedChange={(v) => setDraft((prev) => (prev ? { ...prev, match_enabled: Boolean(v) } : prev))}
                  />
                </div>

                <div className="flex items-center justify-between gap-6 rounded-2xl border border-slate-200 p-4 dark:border-white/10">
                  <div>
                    <p className="font-medium text-slate-800 dark:text-slate-100">Konum Bazlı Eşleşme</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Yakınınızdaki ruh eşlerini bulmak için konumu kullanın.</p>
                  </div>
                  <Switch
                    checked={extrasDraft.location_matching}
                    onCheckedChange={(v) => setExtrasDraft((prev) => ({ ...prev, location_matching: Boolean(v) }))}
                  />
                </div>
              </div>

              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                {[
                  {
                    key: "destiny",
                    title: "Kader Sayısı",
                    description: "Seçtiğin kader sayılarıyla eşleşen profilleri öne çıkarır.",
                    value: matchingPreferencesDraft.prefDestinyNumbers,
                    onChange: (next: number[]) =>
                      setMatchingPreferencesDraft((prev) => ({ ...prev, prefDestinyNumbers: next })),
                  },
                  {
                    key: "heart",
                    title: "Kalp Arzusu",
                    description: "Duygusal ihtiyaç diline göre filtreleme yapar.",
                    value: matchingPreferencesDraft.prefHeartDesireNumbers,
                    onChange: (next: number[]) =>
                      setMatchingPreferencesDraft((prev) => ({ ...prev, prefHeartDesireNumbers: next })),
                  },
                  {
                    key: "expression",
                    title: "İfade Numarası",
                    description: "İletişim ve üretim tarzı benzerliğini dikkate alır.",
                    value: matchingPreferencesDraft.prefExpressionNumbers,
                    onChange: (next: number[]) =>
                      setMatchingPreferencesDraft((prev) => ({ ...prev, prefExpressionNumbers: next })),
                  },
                  {
                    key: "karmic",
                    title: "Karmik Borç",
                    description: "Seçili karmik kodları taşıyan profilleri gösterir.",
                    value: matchingPreferencesDraft.prefKarmicDebtNumbers,
                    onChange: (next: number[]) =>
                      setMatchingPreferencesDraft((prev) => ({ ...prev, prefKarmicDebtNumbers: next })),
                  },
                ].map((group) => (
                  <div key={group.key} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-white/10 dark:bg-white/5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{group.title}</div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{group.description}</div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-8 rounded-full px-3 text-xs"
                        onClick={() => group.onChange([])}
                        disabled={group.value.length === 0}
                      >
                        Temizle
                      </Button>
                    </div>

                    <ToggleGroup
                      type="multiple"
                      value={group.value.map(String)}
                      onValueChange={(values) =>
                        group.onChange(
                          Array.from(
                            new Set(
                              (values ?? [])
                                .map((v) => Math.trunc(Number(v)))
                                .filter((n) => Number.isFinite(n) && n >= 1 && n <= 9),
                            ),
                          ),
                        )
                      }
                      className="mt-3 flex flex-wrap justify-start gap-2"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                        <ToggleGroupItem
                          key={`${group.key}-${digit}`}
                          value={String(digit)}
                          aria-label={`${group.title} ${digit}`}
                          className="h-auto rounded-xl px-3 py-2"
                        >
                          <div className="flex flex-col items-center">
                            <div className="text-sm font-semibold">{digit}</div>
                            <div className="text-[11px] text-muted-foreground">{getDigitShortLabel(digit)}</div>
                          </div>
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                  </div>
                ))}
              </div>
            </Card>
          </section>

          <section className={cn(activeTab === "interests" ? "block" : "hidden")}>
            <Card
              className={cn(
                "rounded-3xl border-slate-100 bg-white p-8 shadow-sm dark:border-[var(--panel-primary)]/10 dark:bg-slate-900",
                highlightField === "interests" && "ring-2 ring-amber-400 ring-offset-2 ring-offset-white",
              )}
            >
              <div className="mb-8 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-[var(--panel-primary)]">✦</span>
                  <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">İlgi Alanları</h2>
                </div>

                <button
                  type="button"
                  onClick={() => toast({ title: "Yakında", description: "İlgi alanlarını yönetme ekranını genişleteceğiz." })}
                  className="text-xs font-bold text-[var(--panel-primary)] hover:underline"
                >
                  Tümünü Gör
                </button>
              </div>

              <div className="flex flex-wrap gap-3">
                {extrasDraft.interests.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-2 rounded-full border border-[var(--panel-primary)]/20 bg-[var(--panel-primary)]/10 px-5 py-2.5 text-sm font-medium text-[var(--panel-primary)]"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() =>
                        setExtrasDraft((prev) => ({
                          ...prev,
                          interests: prev.interests.filter((it) => it !== tag),
                        }))
                      }
                      className="grid size-5 place-items-center rounded-full bg-white/60 text-slate-700 transition-colors hover:bg-white"
                      aria-label={`${tag} kaldır`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}

                {interestSuggestions
                  .filter((tag) => !extrasDraft.interests.includes(tag))
                  .slice(0, 6)
                  .map((tag) => (
                    <button
                      type="button"
                      key={tag}
                      onClick={() =>
                        setExtrasDraft((prev) => ({
                          ...prev,
                          interests: Array.from(new Set([...prev.interests, tag])),
                        }))
                      }
                      className="flex items-center gap-2 rounded-full border border-slate-100 bg-slate-50 px-5 py-2.5 text-sm font-medium text-slate-500 transition-colors hover:border-[var(--panel-primary)]/40 hover:text-[var(--panel-primary)]"
                    >
                      {tag}
                      <span className="text-sm">+</span>
                    </button>
                  ))}
              </div>
            </Card>
          </section>

          <section className={cn(activeTab === "security" ? "block" : "hidden")}>
            <Card className="rounded-3xl border-slate-100 bg-white p-4 shadow-sm sm:p-6 lg:p-8 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
              <div className="mb-5 flex items-center gap-2 sm:mb-8">
                <span className="text-[var(--panel-primary)]">✦</span>
                <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Güvenlik ve Gizlilik</h2>
              </div>

              <div className="space-y-6">
                <div className="rounded-2xl border border-slate-200 p-4 dark:border-white/10">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-800 dark:text-slate-100">Engellediğim Kullanıcılar</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Engel kaldırınca kişi tekrar keşfet ve eşleşme akışına dönebilir.</p>
                    </div>
                  </div>

                  {blockedUsersQuery.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Engellenenler yükleniyor...
                    </div>
                  ) : blockedUsersQuery.data?.length ? (
                    <div className="space-y-2">
                      {blockedUsersQuery.data.map((blockedUser) => (
                        <div
                          key={blockedUser.id}
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 dark:border-white/10 dark:bg-white/5"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            {blockedUser.photoUrl ? (
                              <img src={blockedUser.photoUrl} alt={blockedUser.displayName} className="h-10 w-10 rounded-full object-cover" />
                            ) : (
                              <div className="grid h-10 w-10 place-items-center rounded-full bg-slate-200 text-slate-500">
                                <UserIcon className="h-5 w-5" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{blockedUser.displayName}</div>
                              <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                                @{blockedUser.handle || blockedUser.id.slice(0, 8)}
                              </div>
                            </div>
                          </div>

                          <Button
                            type="button"
                            variant="outline"
                            disabled={unblockUserMutation.isPending}
                            onClick={() => unblockUserMutation.mutate(blockedUser.id)}
                            className="h-8 rounded-lg border-[var(--panel-primary)]/30 text-xs text-[var(--panel-primary)] hover:bg-[var(--panel-primary)]/5"
                          >
                            Engeli Kaldır
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 dark:text-slate-400">Şu anda engellediğiniz biri yok.</div>
                  )}
                </div>

                <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4 dark:bg-white/5">
                  <div>
                    <p className="font-medium text-slate-800 dark:text-slate-100">Şifre Değiştir</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Hesap güvenliğinizi korumak için periyodik olarak güncelleyin.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => toast({ title: "Yakında", description: "Şifre değiştirme entegrasyonunu ekleyeceğiz." })}
                    className="h-10 rounded-xl border-[var(--panel-primary)]/20 text-[var(--panel-primary)] hover:bg-[var(--panel-primary)]/5"
                  >
                    Güncelle
                  </Button>
                </div>

              </div>
            </Card>
          </section>
        </div>

        <aside className="space-y-6">
          <Card className="rounded-3xl border-0 bg-[var(--panel-primary)] p-5 text-white shadow-xl shadow-[color:rgba(55,19,236,0.20)] sm:p-6 lg:p-8">
            <h3 className="text-xl font-bold tracking-tight">Profil Gücü</h3>

            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-wider opacity-80">
                <span>Tamamlanma</span>
                <span>%{completeness.percent}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-white/20">
                <div className="h-full bg-white" style={{ width: `${completeness.percent}%` }} />
              </div>
            </div>

            {missingFields.length > 0 ? (
              <p className="mt-6 text-sm leading-relaxed opacity-90">
                Profilini %100 tamamlayarak keşfet bölümünde daha doğru frekansta buluşabilirsin.
              </p>
            ) : null}

            {missingFields.length > 0 ? (
              <Button
                type="button"
                onClick={onCompleteMissing}
                className="mt-6 h-12 w-full rounded-2xl bg-white font-bold text-[var(--panel-primary)] hover:bg-slate-50"
              >
                Eksikleri Tamamla
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : null}
          </Card>

          <Card className="rounded-3xl border-slate-100 bg-white p-6 shadow-sm dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100">Faz 3 • Gelişim</h3>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Unvan, aura ve son XP kazanımları</p>
              </div>
              <div className="rounded-xl bg-[var(--panel-primary)]/10 px-2.5 py-1 text-xs font-bold text-[var(--panel-primary)]">
                {Math.max(0, Number(progressSummary?.totals?.total_xp ?? 0))} XP
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200/80 p-3 dark:border-white/10">
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {progressSummary?.phase?.rank?.current?.label ?? "Tohum"}
              </div>
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {progressSummary?.phase?.aura?.label ?? "Aura Seviye 1"} • Seri {progressSummary?.phase?.streak?.current ?? 0} gün
              </div>

              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                <div
                  className="h-full bg-[var(--panel-primary)] transition-all"
                  style={{ width: `${Math.max(0, Math.min(100, Number(progressSummary?.phase?.rank?.progress ?? 0)))}%` }}
                />
              </div>
              <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                {progressSummary?.phase?.rank?.next
                  ? `Sonraki unvan: ${progressSummary.phase.rank.next.label} • ${Math.max(0, Number(progressSummary.phase.rank.xpToNext ?? 0))} XP`
                  : "Maksimum unvana ulaştın"}
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Rozetler</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {progressBadges.length ? (
                  progressBadges.slice(0, 6).map((badge) => (
                    <span key={badge.key} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
                      {badge.label}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-slate-500 dark:text-slate-400">İlk rozetler burada görünecek.</span>
                )}
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Son Kazanımlar</div>
              <div className="mt-2 space-y-2">
                {progressEvents.length ? (
                  progressEvents.map((item) => {
                    const occurredAt = String(item.occurredAt ?? "").trim();
                    const d = occurredAt ? new Date(occurredAt) : null;
                    const timeLabel = d && Number.isFinite(d.getTime())
                      ? new Intl.DateTimeFormat("tr-TR", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        }).format(d)
                      : "";

                    return (
                      <div key={item.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs dark:border-white/10 dark:bg-white/5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate pr-2 text-slate-700 dark:text-slate-200">{item.label ?? "Aktivite"}</span>
                          <span className="font-bold text-[var(--panel-primary)]">+{Math.max(0, Number(item.awardedXp ?? 0))} XP</span>
                        </div>
                        {(item.source || timeLabel) ? (
                          <div className="mt-1 truncate text-[11px] text-slate-500 dark:text-slate-400">
                            {item.source ? item.source : "Kaynak"}
                            {timeLabel ? ` • ${timeLabel}` : ""}
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-xs text-slate-500 dark:text-slate-400">Henüz XP geçmişi oluşmadı.</div>
                )}
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-100 bg-white p-8 shadow-sm dark:border-[var(--panel-primary)]/10 dark:bg-slate-900">
            <h3 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Hesap Özeti</h3>

            <ul className="mt-6 space-y-4">
              <li className="flex items-center justify-between border-b border-slate-50 py-2 text-sm dark:border-white/5">
                <span className="text-slate-500 dark:text-slate-400">Üyelik Tipi</span>
                <span className="font-bold text-[var(--panel-primary)]">Üye</span>
              </li>
              <li className="flex items-center justify-between border-b border-slate-50 py-2 text-sm dark:border-white/5">
                <span className="text-slate-500 dark:text-slate-400">Katılım Tarihi</span>
                <span className="font-bold text-slate-900 dark:text-slate-100">{joinDateLabel}</span>
              </li>
              <li className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-500 dark:text-slate-400">Eşleşme Sayısı</span>
                <span className="font-bold text-slate-900 dark:text-slate-100">{statsQuery.data ? statsQuery.data.mutualMatches : "—"}</span>
              </li>
            </ul>

            {statsQuery.data ? (
              <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-xs text-slate-600 dark:bg-white/5 dark:text-slate-300">
                Beğeni: {statsQuery.data.likesGiven} • Beğenildin: {statsQuery.data.likesReceived}
              </div>
            ) : null}
          </Card>

          <div className="flex flex-col gap-4">
            <Button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={!canSave}
              className="h-14 w-full rounded-2xl bg-slate-900 text-white shadow-xl shadow-slate-200 transition-transform hover:scale-[1.02] active:scale-95"
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Değişiklikleri Kaydet
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={resetDraft}
              className="h-14 w-full rounded-2xl border-slate-200 bg-white font-bold text-slate-500 hover:bg-slate-50"
            >
              İptal Et
            </Button>

            <button
              type="button"
              onClick={() => toast({ title: "Yakında", description: "Hesap silme için doğrulama akışı ekleyeceğiz." })}
              className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-red-400 transition-colors hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
              Hesabı Kalıcı Olarak Sil
            </button>
          </div>
        </aside>
      </div>

      {profileQuery.isError ? (
        <div className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Profil yüklenemedi: {profileQuery.error instanceof Error ? profileQuery.error.message : "Bilinmeyen hata"}
        </div>
      ) : null}

      <footer className="mt-10 border-t border-slate-200 pt-6 text-center sm:mt-16 sm:pt-10">
        <p className="text-sm text-slate-400">
          Destek mi gerekiyor?{" "}
          <button
            type="button"
            onClick={() => toast({ title: "Destek Merkezi", description: "Yakında destek sayfasını bağlayacağız." })}
            className="font-semibold text-[var(--panel-primary)] hover:underline"
          >
            Yardım Merkezi'ne göz atın
          </button>
        </p>
      </footer>
    </div>
  );
}
